/**
 * Zod schemas for validation (imports, forms, API responses)
 */
import { z } from 'zod';

// ============================================================================
// Enum Schemas
// ============================================================================

export const ColorSchema = z.enum(['W', 'U', 'B', 'R', 'G']);

export const RaritySchema = z.enum(['common', 'uncommon', 'rare', 'mythic', 'special', 'bonus']);

export const LegalitySchema = z.enum(['legal', 'not_legal', 'restricted', 'banned']);

export const FinishSchema = z.enum(['nonfoil', 'foil', 'etched']);

export const ConditionSchema = z.enum(['M', 'NM', 'LP', 'MP', 'HP', 'D']);

export const LanguageSchema = z.enum(['en', 'es', 'fr', 'de', 'it', 'pt', 'ja', 'ko', 'ru', 'zhs', 'zht', 'ph']);

export const ImportSourceSchema = z.enum([
  'manual',
  'unknown',
  'dragonshield',
  'delverlens',
  'delverlens-legacy',
  'moxfield',
  'archidekt',
  'manabox',
]);

// ============================================================================
// Import Row Schemas (for parsing CSV/text imports)
// ============================================================================

// Legacy per-source row schemas (DragonShieldRowSchema, DelverLensRowSchema,
// DelverLensV2RowSchema, MoxfieldRowSchema) have been removed.
// CSV parsing is now handled by the universal CSV parser in lib/universalCSVParser.ts
// which uses header alias maps and source-specific value transforms.

/**
 * MTGA text format line
 * 4 Lightning Bolt (M10) 146
 * Pattern: <quantity> <card name> [(<set code>) <collector number>]
 */
export const MtgaLineSchema = z
  .string()
  .transform((line) => {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('//') || trimmed.startsWith('#')) {
      return null;
    }

    // Match: "4 Lightning Bolt (M10) 146" or "4 Lightning Bolt"
    const fullMatch = trimmed.match(/^(\d+)\s+(.+?)\s+\(([A-Za-z0-9]+)\)\s+(\S+)$/);
    if (fullMatch) {
      return {
        quantity: parseInt(fullMatch[1], 10),
        name: fullMatch[2].trim(),
        setCode: fullMatch[3].toLowerCase(),
        collectorNumber: fullMatch[4],
      };
    }

    // Match: "4 Lightning Bolt" (no set info)
    const simpleMatch = trimmed.match(/^(\d+)\s+(.+)$/);
    if (simpleMatch) {
      return {
        quantity: parseInt(simpleMatch[1], 10),
        name: simpleMatch[2].trim(),
        setCode: null,
        collectorNumber: null,
      };
    }

    return null;
  })
  .nullable();

export type MtgaLine = NonNullable<z.infer<typeof MtgaLineSchema>>;

// ============================================================================
// Normalized Import Card (common format after parsing)
// ============================================================================

export const ImportCardSchema = z.object({
  name: z.string().min(1),
  setCode: z.string().min(1).toLowerCase().nullable(),
  collectorNumber: z.string().nullable(),
  quantity: z.number().int().min(1),
  finish: FinishSchema.default('nonfoil'),
  condition: ConditionSchema.default('NM'),
  language: LanguageSchema.default('en'),
  purchasePrice: z.number().nullable().default(null),
  purchaseCurrency: z.string().nullable().default(null),
  purchaseDate: z.string().nullable().default(null),
  isAltered: z.boolean().default(false),
  isPlaytest: z.boolean().default(false),
  folderName: z.string().nullable().default(null),
  sourceId: z.string().nullable().default(null),
  // Direct Scryfall ID from source (for matching)
  scryfallId: z.string().nullable().default(null),
});

export type ImportCard = z.infer<typeof ImportCardSchema>;

// ============================================================================
// Collection Card Creation Schema
// ============================================================================

export const CreateCollectionCardSchema = z.object({
  scryfall_id: z.string().uuid(),
  quantity: z.number().int().min(1),
  finish: FinishSchema.default('nonfoil'),
  condition: ConditionSchema.default('NM'),
  language: LanguageSchema.default('en'),
  purchase_price: z.number().nullable().optional(),
  purchase_currency: z.string().nullable().optional(),
  purchase_date: z.string().nullable().optional(),
  is_altered: z.boolean().default(false),
  is_playtest: z.boolean().default(false),
  is_signed: z.boolean().default(false),
  notes: z.string().nullable().optional(),
  folder_id: z.string().nullable().optional(),
  source_system: ImportSourceSchema.default('manual'),
  source_id: z.string().nullable().optional(),
});

export type CreateCollectionCard = z.infer<typeof CreateCollectionCardSchema>;

// ============================================================================
// Folder Schema
// ============================================================================

export const CreateFolderSchema = z.object({
  name: z.string().min(1).max(100),
  color: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .nullable()
    .optional(),
  icon: z.string().nullable().optional(),
  parent_id: z.string().nullable().optional(),
});

export type CreateFolder = z.infer<typeof CreateFolderSchema>;

// ============================================================================
// Search/Filter Schema
// ============================================================================

/** Card supertypes that can be filtered separately */
export const CardSuperTypeFilterSchema = z.enum(['legendary', 'snow', 'basic', 'world', 'ongoing']);

export type CardSuperTypeFilter = z.infer<typeof CardSuperTypeFilterSchema>;

/** Card types that can be parsed from type_line */
export const CardTypeSchema = z.enum(['creature', 'instant', 'sorcery', 'enchantment', 'artifact', 'land', 'planeswalker', 'battle']);

export type CardType = z.infer<typeof CardTypeSchema>;

/** Special color filters for multicolor/colorless */
export const SpecialColorSchema = z.enum(['multicolor', 'colorless']);

export type SpecialColor = z.infer<typeof SpecialColorSchema>;

/** Filter for traditional vs nontraditional cards (CR 108.2a) */
export const CardTraditionFilterSchema = z.enum(['all', 'traditional', 'nontraditional']);

export type CardTraditionFilter = z.infer<typeof CardTraditionFilterSchema>;

/** Card category filter values — 'traditional' or specific nontraditional types */
export const CardCategoryFilterSchema = z.string();

export type CardCategoryFilter = z.infer<typeof CardCategoryFilterSchema>;

/** Rarity filter values (includes 'other' for special/bonus rarities) */
export const RarityFilterSchema = z.enum(['common', 'uncommon', 'rare', 'mythic', 'other']);

export type RarityFilter = z.infer<typeof RarityFilterSchema>;

/** CMC filter values for mana value filtering */
export const CmcFilterSchema = z.union([
  z.literal(0),
  z.literal(1),
  z.literal(2),
  z.literal(3),
  z.literal(4),
  z.literal(5),
  z.literal('6+'),
]);

export type CmcFilter = z.infer<typeof CmcFilterSchema>;

/** Deck membership filter for filtering cards by deck inclusion */
export const InDeckFilterSchema = z.enum(['in-deck', 'not-in-deck']);

/** List membership filter for filtering cards by list inclusion */
export const InListFilterSchema = z.enum(['in-list', 'not-in-list']);

export type InDeckFilter = z.infer<typeof InDeckFilterSchema>;

export type InListFilter = z.infer<typeof InListFilterSchema>;

export const CollectionFiltersSchema = z.object({
  search: z.string().optional(),
  colors: z.array(ColorSchema).optional(),
  colorMode: z.enum(['any', 'all', 'exact', 'identity']).default('any'),
  specialColors: z.array(SpecialColorSchema).optional(),
  rarities: z.array(RarityFilterSchema).optional(),
  sets: z.array(z.string()).optional(),
  types: z.array(z.string()).optional(),
  superTypes: z.array(CardSuperTypeFilterSchema).optional(),
  cardTypes: z.array(CardTypeSchema).optional(),
  cardCategories: z.array(CardCategoryFilterSchema).optional(),
  cmcValues: z.array(CmcFilterSchema).optional(),
  formats: z
    .array(z.string())
    .optional()
    .transform((v) => v as string[] | undefined),
  finishes: z.array(FinishSchema).optional(),
  conditions: z.array(ConditionSchema).optional(),
  folderId: z.string().nullable().optional(),
  priceMin: z.number().optional(),
  priceMax: z.number().optional(),
  starredOnly: z.boolean().optional(),
  gameChangersOnly: z.boolean().optional(),
  availableOnly: z.boolean().optional(),
  inListFilter: InListFilterSchema.optional(),
  listIdFilter: z.string().optional(),
  inDeckFilter: InDeckFilterSchema.optional(),
  deckIdFilter: z.string().optional(),
  roles: z.array(z.string()).optional(),
  keywords: z.array(z.string()).optional(),
  colorIdentities: z.array(z.string()).optional(),
});

export type CollectionFilters = z.infer<typeof CollectionFiltersSchema>;

// ============================================================================
// Deck Schemas
// ============================================================================

export const DeckStatusSchema = z.enum(['brewing', 'active', 'archived']);

export const DeckFormatSchema = z.enum([
  'standard',
  'pioneer',
  'modern',
  'legacy',
  'vintage',
  'pauper',
  'commander',
  'brawl',
  'oathbreaker',
  'casual',
  'draft',
  'sealed',
  'jumpstart',
]);

export const DeckZoneSchema = z.enum(['commander', 'companion', 'main', 'sideboard', 'maybeboard', 'tokens']);

export const CreateDeckSchema = z.object({
  name: z.string().min(1).max(100),
  description: z.string().max(1000).nullable().optional(),
  format: DeckFormatSchema.nullable().optional(),
  status: DeckStatusSchema.default('brewing'),
  enforce_legality: z.boolean().default(true),
});

export type CreateDeck = z.infer<typeof CreateDeckSchema>;

export const UpdateDeckSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  description: z.string().max(1000).nullable().optional(),
  format: DeckFormatSchema.nullable().optional(),
  status: DeckStatusSchema.optional(),
  enforce_legality: z.boolean().optional(),
  cover_card_id: z.string().nullable().optional(),
});

export type UpdateDeck = z.infer<typeof UpdateDeckSchema>;

export const AddDeckCardSchema = z.object({
  scryfall_id: z.string(),
  quantity: z.number().int().min(1).default(1),
  zone: DeckZoneSchema.default('main'),
});

export type AddDeckCard = z.infer<typeof AddDeckCardSchema>;

export const UpdateDeckCardSchema = z.object({
  quantity: z.number().int().min(0).optional(), // 0 = remove
  zone: DeckZoneSchema.optional(),
});

export type UpdateDeckCard = z.infer<typeof UpdateDeckCardSchema>;

// ============================================================================
// List Schemas
// ============================================================================

export const ListPrioritySchema = z.enum(['high', 'medium', 'low']);

export type ListPriorityType = z.infer<typeof ListPrioritySchema>;

export const CreateListSchema = z.object({
  name: z.string().min(1).max(100),
  color: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .nullable()
    .optional(),
  icon: z.string().nullable().optional(),
});

export type CreateList = z.infer<typeof CreateListSchema>;

export const UpdateListSchema = z.object({
  name: z.string().min(1).max(100).optional(),
  color: z
    .string()
    .regex(/^#[0-9A-Fa-f]{6}$/)
    .nullable()
    .optional(),
  icon: z.string().nullable().optional(),
});

export type UpdateList = z.infer<typeof UpdateListSchema>;

export const AddListCardSchema = z.object({
  list_id: z.string(),
  oracle_id: z.string(),
  name: z.string().min(1),
  scryfall_id: z.string().optional(),
  quantity: z.number().int().min(1).default(1),
  priority: ListPrioritySchema.default('medium'),
  notes: z.string().nullable().optional(),
});

export type AddListCard = z.infer<typeof AddListCardSchema>;

export const UpdateListCardSchema = z.object({
  quantity: z.number().int().min(0).optional(), // 0 = remove
  priority: ListPrioritySchema.optional(),
  notes: z.string().nullable().optional(),
  scryfall_id: z.string().nullable().optional(),
});

export type UpdateListCard = z.infer<typeof UpdateListCardSchema>;
